import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import {
  DialogContentBase,
  DialogRef,
  DialogService
} from '@progress/kendo-angular-dialog';
import { GridComponent, RemoveEvent } from '@progress/kendo-angular-grid';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import {
  CEAssociation,
  PrescriberDetailsSimple
} from '@module-claims/models/prescriber.interface';
import { ClaimsService } from '@module-claims/services/claims.service';
import { IAsyncData } from '@module-claims/store/claims.state';
import { ENUM_ROLES } from '@module-role/enums/enum-roles';
import { RoleTypeData } from '@module-role/interfaces/role-type-data.interface';
import {
  CEAssociationModalService,
  CEAssociationRemovalModalService
} from '@module-claims/services/prescriber-modal.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-prescriber-details-modal',
  templateUrl: './prescriber-details-modal.component.html',
  styleUrls: ['./prescriber-details-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PrescriberDetailsModalComponent
  extends DialogContentBase
  implements OnInit, OnDestroy
{
  @Input() npi?: string;

  @ViewChild('locationsGrid')
  private set _locationsGridEl(element: GridComponent) {
    if (element && !this.locationsGridEl) {
      this.locationsGridEl = element;
      this.locationsGrid$.next(element);
    }
  }
  locationsGridEl!: GridComponent;
  locationsGrid$ = new Subject<GridComponent>();

  editRoles: RoleTypeData = {
    roles: [
      ENUM_ROLES.RXP_ADMIN,
      ENUM_ROLES.RXP_EXECUTIVE,
      ENUM_ROLES.RXP_STAFF
    ]
  };

  prescriberDetails$: Observable<IAsyncData<PrescriberDetailsSimple>>;
  npiSearch?: string;
  form!: UntypedFormGroup;
  dateFormat = 'M/d/yyyy';
  dateTimeFormat = 'M/d/yyyy h:mm a';
  lastSearchDate?: Date;
  searchSubmitted = false;

  readonly destroy$ = new Subject();

  constructor(
    private _dialogRef: DialogRef,
    private _claimsService: ClaimsService,
    private _dialogService: DialogService,
    private _ceAssociationModalService: CEAssociationModalService,
    private _ceAssociationRemovalModalService: CEAssociationRemovalModalService,
    private _fb: UntypedFormBuilder,
    private _cdr: ChangeDetectorRef
  ) {
    super(_dialogRef);
    this.prescriberDetails$ = this._claimsService.prescriberDetails$;
  }

  ngOnInit(): void {
    this._claimsService.clearPrescriberDetails();

    this.form = this._fb.group({
      npi: [this.npi]
    });

    this.getPrescriberDetails();

    combineLatest([this.locationsGrid$, this.prescriberDetails$])
      .pipe(
        filter(([grid, state]) => !!grid && !!state?.data),
        takeUntil(this.destroy$)
      )
      .subscribe(([grid, state]) => {
        this.expandAllRows(grid, state.data!.locations.length);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
    this._claimsService.clearPrescriberDetails();
  }

  public showLocationDetail(dataItem: {
    matchingCEs: CEAssociation[];
  }): boolean {
    return !!dataItem.matchingCEs.length;
  }

  expandAllRows(grid: GridComponent, length: number): void {
    for (let i = 0; i < length; i++) {
      grid?.expandRow(i);
    }
    this._cdr.detectChanges();
  }

  collapseAllRows(grid: GridComponent, length: number): void {
    for (let i = 0; i < length; i++) {
      grid?.collapseRow(i);
    }
    this._cdr.detectChanges();
  }

  getPrescriberDetails(): void {
    const npi = this.form.get('npi')?.value?.trim();

    if (npi) {
      this.searchSubmitted = false;
      this.npiSearch = npi;
      this._claimsService.clearPrescriberDetails();
      this._claimsService.getPrescriberDetails(npi).subscribe(() => {
        this.searchSubmitted = true;
        this.lastSearchDate = new Date();
        this._cdr.detectChanges();
      });
    }
  }

  addCEAssociation(): void {
    if (!this.npiSearch) {
      return;
    }

    const sub$ = this._ceAssociationModalService
      .open(this.npiSearch!)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data) => {
          if (data.action === 'save') {
            return this._claimsService
              .addPrescriberCEAssociation(data.data!)
              .pipe(
                map(() => ({ data })),
                catchError(() => {
                  return of({ data: null });
                })
              );
          }
          return of({ data });
        }),
        filter(({ data }) => !!data)
      )
      .subscribe(({ data }) => {
        if (data?.action === 'save') {
          this._claimsService.getPrescriberDetails(this.npiSearch!);
        }
        sub$.unsubscribe();
        data?.modalRef.close();
      });
  }

  addCEAssociationRemoval(): void {
    if (!this.npiSearch) {
      return;
    }

    const sub$ = this._ceAssociationRemovalModalService
      .open(this.npiSearch!)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data) => {
          if (data.action === 'save') {
            return this._claimsService
              .addPrescriberCEAssociation(data.data!)
              .pipe(
                map(() => ({ data })),
                catchError(() => {
                  return of({ data: null });
                })
              );
          }
          return of({ data });
        }),
        filter(({ data }) => !!data)
      )
      .subscribe(({ data }) => {
        if (data?.action === 'save') {
          this._claimsService.getPrescriberDetails(this.npiSearch!);
        }
        sub$.unsubscribe();
        data?.modalRef.close();
      });
  }

  removeCEAssociation(event: RemoveEvent, action: 'add' | 'remove'): void {
    if (!this.npiSearch) {
      return;
    }

    const ceAssociation: CEAssociation = event.dataItem;
    const listLabel = action === 'add' ? 'added' : 'removed';
    const ref = this.showConfirm(
      'Remove Covered Entity',
      `Do you wish to remove this covered entity from the ${listLabel} associations list?`
    );
    ref.result
      .pipe(
        filter((result: any) => result.text === 'Yes'),
        takeUntil(this.destroy$),
        switchMap(() =>
          this._claimsService.removePrescriberCEAssociation({
            action,
            npi: this.npiSearch!,
            associationId: ceAssociation.id!
          })
        )
      )
      .subscribe(() => {
        this._claimsService.getPrescriberDetails(this.npiSearch!);
      });
  }

  private showConfirm(title: string, content: string): DialogRef {
    return this._dialogService.open({
      title,
      content,
      actions: [{ text: 'No' }, { text: 'Yes', primary: true }]
    });
  }
}
